/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 */

import { translate } from '../../Internationalization';
import { withOfferService } from '../../../service-container';
import BaseForm from '../../../shared/Forms/BaseForm';
import captureError from '../../../utils/captureError';
import flags from '../../../shared/Flags';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import paths from '../../../config/local/paths';
import React, { useEffect, useMemo, useState } from 'react';
import useGetAdminsPersonalData from '../../../Hooks/Admin/Getters/useGetAdminsPersonalData/useGetAdminsPersonalData';
import useGetAgentsPersonalData from '../../../Hooks/Agent/Getters/useGetAgentsPersonalData/useGetAgentsPersonalData';
import useIsGated from '../../../Hooks/Shared/useIsGated/useIsGated';
import VendorExclusivityLabel from './VendorExclusivityLabel';
import VendorExclusivityRadio from './VendorExclusivityRadio';

const flag = flags.vendor.vendorExclusivity;

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
function VendorExclusivity(props) {
	const [error, setError] = useState(/** @type {string|null}*/ (null));
	const [exclusivity, setExclusivity] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [subscriptionPath, setSubscriptionPath] = useState('');
	const [success, setSuccess] = useState(/** @type {string|null}*/ (null));
	const isAdmin = props.type === 'administrator';
	const admin = useGetAdminsPersonalData(!isAdmin);
	const agent = useGetAgentsPersonalData(isAdmin);
	const isAdminGated = useIsGated(flag, { role: 'admin' });
	const isAgentGated = useIsGated(flag);
	const isGated = isAdmin ? isAdminGated.isGated : isAgentGated.isGated;
	const isGettingData = useMemo(
		() =>
			admin.isGettingData ||
			agent.isGettingData ||
			isAdminGated.isGettingData ||
			isAgentGated.isGettingData,
		[admin, agent, isAdminGated, isAgentGated]
	);

	useEffect(() => {
		setData();
	}, [admin, agent, isAgentGated]);

	function setData() {
		if (isGettingData) {
			return;
		}

		setError(null);

		if (isGated) {
			getSubscriptionPath();
			return;
		}

		const response = isAdmin ? admin?.data : agent?.data;
		setExclusivity(!!response?.networkVendors);
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {Promise<void>}
	 */
	async function handleSubmit(event) {
		const exclusivityValue = event.target.value === 'true';
		if (exclusivity === exclusivityValue) {
			return;
		}

		setSuccess(null);
		setIsSubmitting(true);
		const messagePath = 'global.offers.vendor_exclusivity';

		try {
			await props.offerService.updateNetworkVendors(
				exclusivityValue,
				props.type
			);

			setExclusivity(exclusivityValue);
			setSuccess(translate(`${messagePath}.success_message`));
		} catch (exception) {
			captureError(exception);
			setError(translate(`${messagePath}.error_message`));
		} finally {
			setIsSubmitting(false);
		}
	}

	function getSubscriptionPath() {
		if (!isAdmin) {
			setSubscriptionPath(paths.app.agent.subscription.root);
			return;
		}

		const primaryGroupId = admin?.data?.groups?.find(
			(group) => group.isPrimary
		)?.id;

		if (!primaryGroupId) {
			setError(translate('subscription.error.unable_to_check'));
			captureError(`Admin ${admin?.data?.email} has no primary group.`);
			return;
		}

		setSubscriptionPath(
			`${paths.app.admin.groups.root}/${primaryGroupId}/subscription`
		);
	}

	function renderMessage() {
		if (error) {
			return (
				<div className="mb-5">
					<FormErrorMessages messages={error} />
				</div>
			);
		}

		return (
			<FormSuccessMessages
				heading={translate(
					'global.offers.vendor_exclusivity.success_heading'
				)}
				messages={success}
			/>
		);
	}

	return (
		<div className="border-b-2 border-grey-300 mb-8">
			<BaseForm
				enableReinitialize={true}
				initialValues={{ exclusivity: `${exclusivity}` }}
				noRequiredText={true}
				onSubmit={handleSubmit}
			>
				<fieldset className="mb-6">
					<VendorExclusivityLabel
						isGated={isGated}
						isGettingData={isGettingData}
						subscriptionPath={subscriptionPath}
					/>

					<VendorExclusivityRadio
						disabled={isGated || !!error || isSubmitting}
						isGettingData={isGettingData}
						onSubmit={handleSubmit}
					/>

					<small>
						<span className="font-bold">
							{translate('global.offers.vendor_exclusivity.note')}
						</span>{' '}
						{translate(
							'global.offers.vendor_exclusivity.note_text'
						)}
					</small>
				</fieldset>
			</BaseForm>

			{renderMessage()}
		</div>
	);
}

// @ts-ignore
export default withOfferService(VendorExclusivity);
